import type { MissionDocumentRow, MissionDocumentType } from '@tpct/mission-core/utils/missionStartApi';
import type { LifecycleStepKey } from '../utils/missionLifecycleSteps';

export type MissionState = 'ongoing' | 'completed' | 'closed' | 'archived' | 'stale_period';

export type MissionParticipant = {
  id: number;
  display_name: string;
  role: string;
};

export type MissionBlocker = {
  code: string;
  message: string;
};

export type MissionWorkReadiness = {
  diligence_count?: number;
  todo_count?: number;
  revise_count?: number;
  supervise_count?: number;
  non_applicable_count?: number;
  anomaly_count?: number;
  section_groups_complete?: boolean;
};

export type MissionSummary = {
  mission: {
    id: number;
    company_id: number;
    cabinet_id?: number;
    subject_type?: string;
    tool_slug: string;
    title: string;
    status: string;
    state: MissionState;
    period_start: string;
    period_end: string;
    responsable_user_id?: number | null;
    responsable_display_name?: string | null;
    responsable_role?: string | null;
    current_step_key: string | null;
    archived_at?: string | null;
    retention_until?: string | null;
    can_continue?: boolean;
    can_edit_responsable?: boolean;
    report_generated?: boolean;
    is_frozen?: boolean;
  };
  lifecycle?: {
    status?: string;
    stored_status?: string;
    computed_readiness_status?: string;
    work_readiness?: MissionWorkReadiness;
    blockers?: MissionBlocker[];
    warnings?: MissionBlocker[];
    next_actions?: string[];
    allowed_actions?: string[];
    is_frozen?: boolean;
    readonly_fields?: string[];
    progress_step_key?: LifecycleStepKey;
    signing_available?: boolean;
  };
  steps: Array<{
    key: string;
    label: string;
    url: string;
    status: string;
    enabled: boolean;
    order: number;
  }>;
  next_step_url: string | null;
  current_step: {
    key: string;
    label: string;
    url: string;
    status: string;
    enabled: boolean;
    order: number;
  } | null;
  linked_report_id: number | null;
  documents?: MissionDocumentRow[];
  missing_required_documents?: MissionDocumentType[];
  required_document_types?: MissionDocumentType[];
  publish_catalog?: boolean;
  collaborators?: MissionParticipant[];
};

export type MissionApiConfig = {
  hubBaseUrl: string;
  vsmeBaseUrl: string;
  nonce: string;
};

export const missionApiConfigFromBootstrap = (
  hubBaseUrl: string,
  vsmeBaseUrl: string,
  nonce: string,
): MissionApiConfig => ({
  hubBaseUrl,
  vsmeBaseUrl,
  nonce,
});

export const missionCanEditResponsable = (summary: MissionSummary): boolean =>
  summary.mission.can_edit_responsable === true;

export const missionIsReadOnly = (summary: MissionSummary): boolean => {
  const state = summary.mission.state;
  if (state === 'archived' || state === 'closed') {
    return true;
  }
  if (summary.lifecycle?.is_frozen === true || summary.mission.is_frozen === true) {
    return true;
  }
  return false;
};
